import { Link } from 'react-router-dom';
import PublicLayout from '../layouts/PublicLayout';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import { 
  DollarSign, 
  Percent, 
  HeartHandshake, 
  Calendar,
  Shield,
  Users,
  ArrowRight,
  Check
} from 'lucide-react';

const BENEFITS = [
  {
    icon: Percent,
    title: 'Lower Fees',
    description: 'Keep more of what you earn. Our competitive 3% host fee is designed to maximize your earnings.',
  },
  {
    icon: HeartHandshake,
    title: 'Local Support',
    description: 'Our team is based in Southwest Florida. We understand the local market and are here when you need us.',
  },
  {
    icon: Calendar,
    title: 'Simple Management',
    description: 'Easy-to-use dashboard for managing your calendar, bookings, and guest communications.',
  },
  {
    icon: Shield,
    title: 'Trusted Guests',
    description: 'Every booking is verified. We attract quality guests who respect your property.',
  },
  {
    icon: Users,
    title: 'Targeted Audience',
    description: 'We focus exclusively on Southwest Florida, bringing you guests specifically looking for SWFL stays.',
  },
  {
    icon: DollarSign,
    title: 'Transparent Pricing',
    description: 'No hidden fees or algorithm games. What you see is what you get, always.',
  },
];

const FEE_COMPARISON = [
  { platform: 'Our Platform', hostFee: '3%', guestFee: '6%', total: '9%' },
  { platform: 'Other Platforms', hostFee: '3-15%', guestFee: '6-12%', total: '15-27%' },
];

const STEPS = [
  { step: 1, title: 'Create Your Listing', description: 'Add your property details, photos, and pricing in minutes.' },
  { step: 2, title: 'Connect Payments', description: 'Set up secure payments to receive your earnings directly.' },
  { step: 3, title: 'Start Hosting', description: 'Publish your listing and start welcoming guests to SWFL.' },
];

export default function HostBenefitsPage() {
  const { isAuthenticated } = useAuth();
  
  // Determine the correct CTA link based on auth state
  const getStartedLink = isAuthenticated 
    ? '/app/host/properties/new' 
    : '/login?next=/app/host/properties/new';

  return (
    <PublicLayout>
      {/* Hero Section */}
      <section className="relative min-h-[60vh] flex items-center gradient-ocean">
        <div className="container-main py-20">
          <div className="max-w-3xl">
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 mb-6">
              List Your Southwest Florida Property
            </h1>
            <p className="text-lg text-slate-600 mb-8">
              Join a community of hosts who value lower fees, local support, and quality guests. 
              Built specifically for Southwest Florida property owners.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to={getStartedLink}>
                <Button className="btn-accent">
                  Get Started
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <Link to="/search">
                <Button variant="outline" className="btn-outline">
                  Browse Properties
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Grid */}
      <section className="section-padding">
        <div className="container-main">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Why Host With Us
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              We're building something different — a platform that puts hosts first.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {BENEFITS.map((benefit, index) => (
              <div key={index} className="feature-card">
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                  <benefit.icon className="w-6 h-6 text-primary" />
                </div>
                <h3 className="font-heading text-xl font-semibold text-slate-900 mb-2">
                  {benefit.title}
                </h3>
                <p className="text-slate-600">
                  {benefit.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Fee Comparison */}
      <section className="section-padding bg-slate-900 text-white">
        <div className="container-main">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-4">
              Transparent Fee Structure
            </h2>
            <p className="text-slate-300 max-w-2xl mx-auto">
              We believe in honest pricing. Here's how our fees compare.
            </p>
          </div>

          <div className="max-w-2xl mx-auto bg-white rounded-xl overflow-hidden">
            <table className="w-full">
              <thead className="bg-slate-100">
                <tr>
                  <th className="px-6 py-4 text-left text-slate-900 font-semibold">Platform</th>
                  <th className="px-6 py-4 text-center text-slate-900 font-semibold">Host Fee</th>
                  <th className="px-6 py-4 text-center text-slate-900 font-semibold">Guest Fee</th>
                  <th className="px-6 py-4 text-center text-slate-900 font-semibold">Total</th>
                </tr>
              </thead>
              <tbody>
                {FEE_COMPARISON.map((row, index) => (
                  <tr key={index} className={index === 0 ? 'bg-primary/5' : ''}>
                    <td className="px-6 py-4 text-slate-900 font-medium">
                      {index === 0 && <Check className="w-4 h-4 text-green-600 inline mr-2" />}
                      {row.platform}
                    </td>
                    <td className="px-6 py-4 text-center text-slate-700">{row.hostFee}</td>
                    <td className="px-6 py-4 text-center text-slate-700">{row.guestFee}</td>
                    <td className="px-6 py-4 text-center font-semibold text-slate-900">{row.total}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <p className="text-center text-slate-400 mt-6 text-sm">
            * Other platform fees vary based on their pricing models and policies
          </p>
        </div>
      </section>

      {/* How It Works */}
      <section className="section-padding">
        <div className="container-main">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Get Started in 3 Easy Steps
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {STEPS.map((step) => (
              <div key={step.step} className="text-center">
                <div className="w-16 h-16 rounded-full bg-primary text-white flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                  {step.step}
                </div>
                <h3 className="font-heading text-xl font-semibold text-slate-900 mb-2">
                  {step.title}
                </h3>
                <p className="text-slate-600">
                  {step.description}
                </p>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to={getStartedLink}>
              <Button className="btn-primary">
                Create Your Listing
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding gradient-ocean">
        <div className="container-main text-center">
          <h2 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
            Ready to Start Hosting?
          </h2>
          <p className="text-slate-600 max-w-2xl mx-auto mb-8">
            Join Southwest Florida's dedicated vacation rental marketplace. 
            Lower fees, better support, quality guests.
          </p>
          <Link to={getStartedLink}>
            <Button className="btn-accent">
              Get Started Today
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </section>
    </PublicLayout>
  );
}
